/**
 * UserProfile: The Netx user object
 * @class
 * @todo DAM-8523
 */
import Base from '@netx/core/lib/netx-model';
import GroupsCollection from '../collections/groups';
import {types as userTypes} from '../properties/user';

export default class UserProfile extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'userId';
	}
	/** @type {string} */
	get titleAttribute() {
		return 'login';
	}

	/**
	 * @type {string}
	 * @default
	 * @todo This does not belong here - this belongs in Netx.user.options
	 */
	get resetUrl() {
		return window.location.href.replace(/#.*$/, '') + '#password-reset';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object}  defaults                 - The default attribute values
	 * @property {string}  defaults.address1        - Default address line 1
	 * @property {string}  defaults.address2        - Default address line 2
	 * @property {string}  defaults.city            - Default city
	 * @property {string}  defaults.country         - Default country
	 * @property {string}  defaults.email           - Default email address
	 * @property {string}  defaults.expirationDate  - Default expiration date as an ANSI-formatted date string ("2003-11-13")
	 * @property {array}   defaults.groupIds        - Default group ids this user is a member of
	 * @property {array}   defaults.groupNames      - Default group names this user is a member of
	 * @property {string}  defaults.login           - Default login (username)
	 * @property {string}  defaults.name1           - Default first name
	 * @property {string}  defaults.name2           - Default middle name
	 * @property {string}  defaults.name3           - Default last name
	 * @property {string}  defaults.name4           - Default (???) This value is generally only used with Japanese names
	 * @property {number}  defaults.notify          - Default notify (boolean as number). Signifies whether this user should receive email from the application.
	 * @property {string}  defaults.organization    - Default organization
	 * @property {string}  defaults.password1       - Default password
	 * @property {string}  defaults.password2       - Default password confirmed
	 * @property {string}  defaults.phone1          - Default home phone
	 * @property {string}  defaults.phone2          - Default work phone
	 * @property {string}  defaults.phone3          - Default cell phone
	 * @property {string}  defaults.phone4          - Default (???) phone
	 * @property {array}   defaults.propertyNames   - Default ???
	 * @property {array}   defaults.propertyValues  - Default ???
	 * @property {string}  defaults.state           - Default state (region)
	 * @property {number}  defaults.type            - Default type (user level). See `user/properties/user`
	 * @property {number}  defaults.userId          - Default id
	 * @property {number}  defaults.userSource      - Default user source. 0 - INTERNAL, 1 - LDAP, 2 - SAML, 3 - PING_ONE, 4 LIVE_CONNECT
	 * @property {number}  defaults.zip             - Default postal code
	 *
	 * @property {number}  defaults.category                    - Default upload category. See `this.parsers.getUserUploadCategory`
	 * @property {number}  defaults.minimumPasswordLength       - Minimum password length. See `this.decodeUserToken`
	 * @property {string}  defaults.passwordChange              - ??? Can not find it being utilized anywhere (perhaps it is supposed to be `passwordReset` as seen in `this.passwordChange`? )
	 * @property {string}  defaults.passwordRequest             - A string signifying the "success" of a password request. See `this.passwordRequest`
	 * @property {string}  defaults.registration                - A string signifying the "success" of a registration attempt. See `this.registerNewUser`
	 * @property {string}  defaults.strictPasswordInstructions  - Strict password instructions. See `this.decodeUserToken`
	 */
	get defaults() {
		return {
			address1: '',
			address2: '',
			city: '',
			country: '',
			email: '',
			expirationDate: '',
			groupIds: [],
			groupNames: [],
			login: '',
			name1: '',
			name2: '',
			name3: '',
			name4: '',
			notify: 1,
			organization: '',
			password1: '',
			password2: '',
			phone1: '',
			phone2: '',
			phone3: '',
			phone4: 'NA',
			propertyNames: [],
			propertyValues: [],
			state: 'NA',
			token: '',
			type: -1,
			// userId: 0,
			userSource: 0,
			zip: '',

			// Client side attributes
			category: 0,
			minimumPasswordLength: 8,
			passwordChange: '',
			passwordRequest: '',
			registration: '',
			strictPasswordInstructions: '',
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return [
			'category',
			'minimumPasswordLength',
			'strictPasswordInstructions',
			'passwordRequest',
			'passwordChange',
			'registration',
		];
	}

	/**
	 * @property {object} methods                        - Model RPC methods
	 * @property {method} methods.create                 - Create new user
	 * @property {method} methods.read                   - Fetch user
	 * @property {method} methods.remove                 - Destroy user
	 * @property {method} methods.update                 - Update user
	 * @property {method} methods.authenticate           - Authenticate user
	 * @property {method} methods.getGroupForUser        - Fetch user's groups
	 * @property {method} methods.getSessionKeyForUser   - Fetch user's session key (admin only)
	 * @property {method} methods.getUserUploadCategory  - Fetch user's upload category
	 * @property {method} methods.login                  - Login user
	 * @property {method} methods.logout                 - Lougout user
	 * @property {method} methods.logoutUser             - Logout specific user by id (spiderman pointing at spiderman.jpg)
	 * @property {method} methods.passwordReset          - Step 2 in reset: Update user password via token from `requestPasswordReset`
	 * @property {method} methods.requestPasswordReset   - Step 1 in reset: Requests that a password reset link be emailed to the user
	 * @property {method} methods.setUserGroups          - Update user groups
	 * @property {method} methods.userSelfRegistration   - Register as user
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create: function (opts) {
				return ['setUser', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read: function (opts) {
				if (!this.isMe()) {
					// returns an *array*, see parsers.read()
					return ['getUserObjects', [this.id]];
				}
				return this.getSelf().then((self) => {
					return this._canCreateAPITokens(opts).then((canCreateAPITokens) => {
						this.canCreateAPITokens = canCreateAPITokens;
						return opts?.parse ? this.parsers.read(self, opts) : self;
					});
				});
			},
			_canCreateAPITokens: ['canCreateAPITokens'],
			getSelf: ['getSelf'],
			/** @method */
			// note: according to flex docs, user cannot delete themselves (returns 0).  -1 = fail, 1 = success
			remove: ['deleteUser', 'id'],
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update: function (opts) {
				// DAM-5605
				if (!this.isMe()) {
					return ['setUser', this.self()];
				}
				return ['modifyCurrentUserData', this.self()];
			},

			/** @method */
			authenticate: ['authenticate', 'login', 'password1'],
			/** @method */
			getGroups(opts) {
				return this.getGroupsForUser(opts).then((groupIds) => {
					this.set('groupIds', groupIds);

					this.groups.reset(
						groupIds.map((id) => {
							return {groupId: id};
						}),
						opts,
					);

					this.groups.pager.set(
						{
							currentPage: 1,
							pageSize: groupIds.length,
						},
						opts,
					);

					return this.groups.getGroups(opts);
				});
			},
			/** @method */
			getGroupsForUser(opts) {
				return [
					'getGroupsForUser',
					this.id,
					{name: null},
					{field: 'name', order: 'asc'},
					{startIndex: 0, size: 0x7fff},
				];
			},
			/** @method */
			getSessionKeyForUser: ['getSessionKeyForUser', 'userId'],
			/** @method */
			getUserUploadCategory: ['getUserUploadCategory'],
			/** @method */
			login: ['authenticateAndGetUserBean', 'login', 'password1'],
			/** @method */
			logout: ['logoutUser'],
			/** @method */
			logoutUser: ['logout', 'userId'],
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			passwordReset: function (opts) {
				return [
					'changePassword',
					this.get('login'),
					'',
					this.get('token'),
					this.get('password'),
				];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			requestPasswordReset: function (opts) {
				return ['changePassword', this.get('login'), this.resetUrl, '', ''];
			},
			/**
			 * @method
			 * @param {array} ids Group ids
			 * @param {object} opts Request options
			 */
			setUserGroups(ids, opts) {
				return ['setUserGroups', this.id, ids];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			userSelfRegistration: function (opts) {
				return ['userSelfRegistration', this.self()];
			},
		};
	}

	/**
	 * @property {object}   parsers                        - Model parsers
	 * @property {function} parsers.create                 - Parse from creation of new user
	 * @property {function} parsers.read                   - Parse fetch of user
	 * @property {function} parsers.update                 - Parse update of user
	 * @property {function} parsers.getGroupForUser        - Parse fetch of user's groups
	 * @property {function} parsers.getUserUploadCategory  - Parse fetch of user's upload category
	 * @property {function} parsers.passwordReset          - Parse update of user password via token from `requestPasswordReset`
	 * @property {function} parsers.requestPasswordReset   - Parse request that a password reset link be emailed to the user
	 * @property {function} parsers.userSelfRegistration   - Parse register as user
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {object} data Data (validation bean)
			 * @param {object} opts Request options
			 * @todo Get full beans
			 */
			create: function (data, opts) {
				return {userId: data.id, password1: null, password2: null};
			},
			/**
			 * @function
			 * @param {object} data Data (validation bean)
			 * @param {object} opts Request options
			 */
			read: function (data, opts) {
				// kinda hacktacular, but... ?  getUsers returns an *array* of user object but we just want one.  but getSelf just returns the obj.
				if (data instanceof Array) {
					if (!data.length) return null;

					data[0].password1 = data[0].password2 = null;
					return data[0];
				}

				// modifyCurrentData won't ignore the password fields unless they're set to '********'
				data.password1 = data.password2 = null;
				return data;
			},
			/**
			 * @function
			 * @param {object} data Data
			 * @param {object} opts Request options
			 */
			update: function (data, opts) {
				// get rid of the result entirely, so BackboneRPC doesn't extend it onto Netx.User
				return {
					password1: null,
					password2: null,
				};
			},

			/**
			 * @function
			 * @param {number} categoryId Category id
			 * @param {object} opts Request options
			 */
			getUserUploadCategory: function (categoryId, opts) {
				this.set('category', categoryId, opts);
			},
			/**
			 * @function
			 * @param {boolean} resultBool Result
			 * @param {object} opts Request options
			 * @todo Can someone document why we are doing this?
			 * The code that makes this call would get the `resultBool` and would already have
			 * reference to `this` in order to do so...
			 */
			passwordReset: function (resultBool, opts) {
				return {
					profile: this,
					resultBool: resultBool,
				};
			},
			/**
			 * @function
			 * @param {boolean} resultBool Result
			 * @param {object} opts Request options
			 * @todo Can someone document why we are doing this?
			 * The code that makes this call would get the `resultBool` and would already have
			 * reference to `this` in order to do so...
			 */
			requestPasswordReset: function (resultBool, opts) {
				return {
					profile: this,
					resultBool: resultBool,
				};
			},
			/**
			 * @function
			 * @param {boolean} result Result
			 * @param {array} ids Group ids
			 * @param {object} opts Request options
			 */
			setUserGroups(result, ids, opts) {
				this.set('groupIds', ids, opts);
			},
			/**
			 * @function
			 * @param {null} response Null response
			 * @param {object} opts Request options
			 * @todo Can someone document why we are doing this?
			 * The code that makes this call would already have reference to `this` in order to do so...
			 */
			userSelfRegistration: function (response, opts) {
				return {profile: this};
			},
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		this.canCreateAPITokens = false;

		/**
		 * @memberof module:user/models/userProfile
		 * @alias groups
		 * @type {module:user/collections/groups}
		 */
		this.groups = new GroupsCollection([], {
			comparator: 'name',
			// We do not have a good solution for paging selected items as of yet
			pager: {
				pageSize: 0x7fffffff,
			},
		});
	}

	/**
	 * @method
	 * @return Lexicographic name ("Doe, Jane")
	 */
	getLexicographicName() {
		return _.without(_.values(this.pick('name3', 'name1')), '').join(', ');
	}

	/**
	 * returns true if profile instance is the logined user
	 * @method
	 * @return {boolean}
	 */
	isMe() {
		return Netx.userProfile.id === this.id;
	}

	/**
	 * some(all?) of this will/should be caught via in-form validation js
	 * @todo document
	 */
	// TODO: we generally don't use the validate feature of Backbone.Model, so maybe we should take this out.
	// The form handler does the same kind of checking ...
	//
	validate(attrs, options) {
		var err = [];
		if (attrs.userId < 1) {
			//only check for new user
			if (Netx.system && Netx.system.users && Netx.system.users.length) {
				_.each(Netx.system.users.models, function (u) {
					if (u.get('login').toLowerCase() == attrs.login.toLowerCase()) {
						err.push('Login <b>' + attrs.login + '</b> already in use.');
					}
				});
			}
		}
		if (!/^\S+@\S+\.\S+/.test(attrs.email)) {
			err.push('Invalid email address');
		}
		var req = {
			email: 'email address',
			name1: 'first name',
			login: 'login',
		}; //TODO generalize (e.g. passed from server) - could read from form. :(
		_.each(req, function (name, prop) {
			if (attrs[prop] === '') {
				err.push('you must have a value for ' + name);
			}
		});
		if (attrs.password1 != attrs.password2) {
			err.push('passwords do not match');
		}
		if (err.length > 0) {
			return '<ul><li>' + err.join('</li><li>') + '</li><ul>';
		}
		return false;
	}

	/**
	 * Test for profile usertype by name
	 * @method
	 * @param {string} typeName Type name
	 * @returns {boolean}
	 */
	isType(typeName) {
		// keys in this object are all ucfirst ...
		return (
			this.get('type') == _.invert(userTypes)[_.ucfirst(typeName.toLowerCase())]
		);
	}
	/**
	 * Test for profile usertype by name
	 * @param {string} typeName Type name
	 * @returns {boolean}
	 */
	isTypeOrGreater(typeName) {
		return (
			this.get('type') >= _.invert(userTypes)[_.ucfirst(typeName.toLowerCase())]
		);
	}

	/**
	 * Sets model(itself), calls requestPasswordReset x7 api
	 * @method
	 * @param {object} loginObj Login data
	 */
	userLogin(loginObj, opts) {
		this.set({
			login: loginObj.username,
			password1: loginObj.password,
		});
		return this.authenticate(opts);
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Options
	 */
	parseAndSave(data, opts) {
		// Get only the values pertinant to our model's attributes
		var parsed = _.pick.apply(
			null,
			[data].concat(_.keys(this.constructor.GetDefaults())),
		);

		// No point in setting this here
		// neither setUser nor modifyCurrentUserData sets propertyNames and propertyValues
		// custom user data
		// model.setPropertyArrays(values);

		// Adjust notify boolean to int
		parsed.notify = parsed.notify ? 1 : 0;

		// TODO: https://jira.netx.net/browse/DAM-8541
		if (parseInt(parsed.type) === 0) {
			parsed.type = -1;
		}

		// Password has not changed
		if (
			!data.autogeneratePassword &&
			(parsed.password1 === '' || parsed.password2 === '')
		) {
			parsed = _.omit(parsed, 'password1', 'password2');
		}
		// Autogenerate password (send empty strings to server)
		else if (data.autogeneratePassword) {
			parsed.password1 = parsed.password2 = '';
		}

		return this.set(parsed, opts).save(opts);
	}

	/**
	 * Sets model(itself), calls requestPasswordReset x7 api
	 * @method
	 * @param {object} passObj Login data
	 * @param {object} opts Request options
	 */
	passwordRequest(loginObj, opts) {
		this.set({
			login: loginObj.login,
			passwordRequest: '',
		});
		return this.requestPasswordReset(opts).then(function (response) {
			this.set({
				login: '',
				passwordRequest: response.resultBool ? 'success' : 'failure',
			});
		});
	}

	/**
	 * Sets model(itself), calls passwordReset x7 api
	 * @method
	 * @param {object} passObj Password data
	 * @param {object} opts Request options
	 */
	passwordChange(passObj, opts) {
		this.set({password: passObj.newPassword});
		return this.passwordReset(opts).then(function (response) {
			this.set({passwordReset: response.resultBool ? 'success' : 'failure'});
			return response;
		});
	}

	/**
	 * Sets model(itself) and calls userSelfRegistration x7 api
	 * @method
	 * @param {object} userObj User data
	 * @param {object} opts Request options
	 * todo: have Java send back useful information
	 */
	registerNewUser(userObj, opts) {
		this.set(userObj);
		return this.userSelfRegistration(opts).then(function (response) {
			this.set({registration: 'success'});
			return response;
		});
	}

	self() {
		const self = super.self();
		if (self.password1 == null) self.password1 = '********';
		if (self.password2 == null) self.password2 = '********';
		return self;
	}

	/**
	 * Login as this user in a new browser.
	 * Admin only
	 * @method
	 * @param {object} opts Request options
	 * @returns {string} key
	 */
	loginAs(opts) {
		return this.getSessionKeyForUser(opts);
	}

	/**
	 * Handles decoding the token for password restrictions if there are any
	 * @param {object} loginTokenObj Login token data
	 */
	decodeUserToken(loginTokenOb) {
		this.set(loginTokenOb);
		var decodedTokenArr = window
			.atob(decodeURIComponent(this.get('token')))
			.split('~');

		// did java pass strict password info?
		if (decodedTokenArr.length > 3) {
			this.set({
				minimumPasswordLength: decodedTokenArr[2].length
					? parseInt(decodedTokenArr[2])
					: 8,
			});

			var strictMsg = '';

			// required password pattern
			if (decodedTokenArr[3].length) {
				_.each(
					decodedTokenArr[3].split(','),
					function (pat, idx, all) {
						var value = parseInt(pat.slice(0, -1)),
							leadIn =
								idx === all.length - 1
									? ' ' + i18n.t('i18n.passwordAnd') + ' '
									: idx > 0
									? ', '
									: ' ',
							i18nString = value > 1 ? '_plural' : '';
						switch (pat.slice(-1)) {
							// required number count
							case 'N':
								i18nString = 'i18n.passwordNumberCount' + i18nString;
								break;
							// required lowercase count
							case 'L':
								i18nString = 'i18n.passwordLowercaseCount' + i18nString;
								break;
							// required uppercase count
							case 'U':
								i18nString = 'i18n.passwordUppercaseCount' + i18nString;
								break;
							// required special character count
							case 'S':
								i18nString = 'i18n.passwordSpecialCount' + i18nString;
								break;
						}

						strictMsg += leadIn + i18n.t(i18nString, {count: value});
					},
					this,
				);
			}

			if (strictMsg.length) {
				this.set({
					strictPasswordInstructions:
						i18n.t('i18n.passwordStrict') + ' ' + strictMsg,
				});
			}
		}
	}

	/**
	 * We have a fun issue with users
	 * Our autocompletes do not return `login` but full names
	 * We want labels to match up between a select from a collection
	 * and a select from an autolookup - so...
	 * @override
	 * @return {string} Autocomplete label
	 */
	getAutocompleteLabel() {
		return this.fullName() || this.getLabel();
	}

	/**
	 * Converts propertyNames/propertyValues parallel arrays into usable object
	 * @method
	 * @returns {object} formData
	 */
	getPropertyObject() {
		// create a userValues object for the CustomForm collection
		var formData = {},
			values = this.get('propertyValues');
		_.each(this.get('propertyNames'), function (propName, idx, all) {
			formData[propName] = values[idx];
		});

		return formData;
	}
	/**
	 * Converts form data object into propertyNames/propertyValues parallel arrays
	 * and sets them
	 * @method
	 * @param {object} values Form values
	 */
	setPropertyArrays(values) {
		// overriding to store custom fields, the dam just loves parallel arrays
		var propertyNames = [],
			propertyValues = [],
			modelAttrs = [];

		_.each(
			values,
			_.bind(function (empty, idx, all) {
				var prefix = idx.slice(0, 4),
					attrName = idx.slice(4),
					isOurAttr;

				if (prefix === 'cfe-') {
					isOurAttr = _.keys(this.attributes).indexOf(attrName) !== -1;
					if (!isOurAttr) {
						propertyNames.push(attrName);
						propertyValues.push(all[idx]);
					}
				} else {
					attrName = idx;
					isOurAttr = _.keys(this.attributes).indexOf(attrName) !== -1;
				}

				if (isOurAttr) {
					modelAttrs[attrName] = all[idx];
				}
			}, this),
		);

		this.set('propertyNames', propertyNames);
		this.set('propertyValues', propertyValues);

		return modelAttrs;
	}

	/**
	 * Return a 'full name' string for the user.
	 *
	 * The DAM maintains 4 'name' fields (name1 - name4) but our UI only uses two of them (name1 & name3).
	 * Other names (middle name, suffix) might exist out there somewhere, so we check for those too.
	 *
	 * @method
	 * @returns {string} Full name.
	 */
	fullName() {
		return (
			this.chain()
				// Get an object containing the four name fields:
				.pick('name1', 'name2', 'name3', 'name4')
				// Get an array of just the values (not the keys)
				.values()
				// Take out any empty strings
				.without('')
				// Join the rest with single spaces
				.join(' ')
				// Finish the chain().
				.value()
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'UserProfile';
	}
}
